import { useState } from "react";
import { useAuth } from "../lib/AuthContext";

const COLORS = {
  bg: "#F8F7F4",
  surface: "#FFFFFF",
  surfaceAlt: "#F1F0EC",
  border: "#E2E0DA",
  text: "#1A1917",
  textSecondary: "#6B6860",
  textMuted: "#9C9889",
  accent: "#2B5CE6",
  accentLight: "#EBF0FD",
  red: "#C53030",
  redLight: "#FEE7E7",
};

const ENGAGEMENT_TYPES = [
  { key: "internship", label: "Internship" },
  { key: "part_time", label: "Part-time job" },
  { key: "graduate_program", label: "Full-time graduate program" },
  { key: "full_time", label: "Full-time position" },
  { key: "project", label: "Project" },
];

const STUDENT_COUNTS = [
  { key: "1-2", label: "1–2 students" },
  { key: "3-5", label: "3–5 students" },
  { key: "6-10", label: "6–10 students" },
  { key: "10+", label: "10+ students" },
];

export default function OnboardingForm() {
  const { session, refreshProfile, signOut, profile } = useAuth();
  const [engagementType, setEngagementType] = useState("");
  const [studentCount, setStudentCount] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!engagementType) {
      return setError("Please select an engagement type.");
    }
    if (!studentCount) {
      return setError("Please select how many students you're looking for.");
    }
    if (!roleDescription.trim()) {
      return setError("Please describe what you're looking for.");
    }
    setSubmitting(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || "https://api.careersorter.co";
      const res = await fetch(`${API_URL}/auth/complete-onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          engagement_types: [engagementType],
          student_count: studentCount,
          role_description: roleDescription.trim(),
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to save");
      }
      await refreshProfile();
    } catch (err) {
      setError(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const RadioGroup = ({ options, value, onChange, label }) => (
    <div style={{ marginBottom: 24 }}>
      <label style={{
        display: "block", fontSize: 14, fontWeight: 600,
        color: COLORS.text, marginBottom: 12,
      }}>
        {label}
      </label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {options.map(opt => {
          const selected = value === opt.key;
          return (
            <button
              type="button"
              key={opt.key}
              onClick={() => onChange(opt.key)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 16px", borderRadius: 8,
                border: `1.5px solid ${selected ? COLORS.accent : COLORS.border}`,
                backgroundColor: selected ? COLORS.accentLight : COLORS.surface,
                cursor: "pointer", fontFamily: "inherit",
                transition: "all 0.15s", textAlign: "left",
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: "50%",
                border: `2px solid ${selected ? COLORS.accent : COLORS.border}`,
                backgroundColor: "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all 0.15s",
              }}>
                {selected && (
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%",
                    backgroundColor: COLORS.accent,
                  }} />
                )}
              </div>
              <span style={{
                fontSize: 14, fontWeight: selected ? 600 : 500,
                color: selected ? COLORS.accent : COLORS.text,
              }}>
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      backgroundColor: COLORS.bg,
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 520 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 12,
          }}>R</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Welcome to Relay
          </h1>
          <p style={{ fontSize: 13, color: COLORS.textMuted, margin: 0 }}>
            Tell us about your engagement with the school
          </p>
        </div>

        {/* Card */}
        <div style={{
          backgroundColor: COLORS.surface,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          padding: 28,
        }}>
          <form onSubmit={handleSubmit}>
            <RadioGroup
              label="What type of engagement are you looking for?"
              options={ENGAGEMENT_TYPES}
              value={engagementType}
              onChange={setEngagementType}
            />

            <RadioGroup
              label="How many students are you looking for?"
              options={STUDENT_COUNTS}
              value={studentCount}
              onChange={setStudentCount}
            />

            {/* Role Description */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: "block", fontSize: 14, fontWeight: 600,
                color: COLORS.text, marginBottom: 8,
              }}>
                Describe what you're looking for
              </label>
              <p style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>
                Role details, skills, programme preferences, or anything that helps the careers team understand your needs.
              </p>
              <textarea
                value={roleDescription}
                onChange={e => setRoleDescription(e.target.value)}
                placeholder="e.g. We're looking for MSc Finance or MBA graduates for our summer analyst programme in London. Strong analytical and communication skills preferred."
                rows={5}
                style={{
                  width: "100%", padding: "12px 14px", borderRadius: 8,
                  border: `1px solid ${COLORS.border}`, fontSize: 14,
                  fontFamily: "inherit", outline: "none", resize: "vertical",
                  backgroundColor: COLORS.surface, color: COLORS.text,
                  lineHeight: 1.6, boxSizing: "border-box",
                }}
              />
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: "10px 14px", borderRadius: 8, marginBottom: 16,
                backgroundColor: COLORS.redLight, color: COLORS.red, fontSize: 13,
              }}>{error}</div>
            )}

            {/* Submit */}
            <button type="submit" disabled={submitting} style={{
              width: "100%", padding: "12px 0", borderRadius: 8, border: "none",
              backgroundColor: COLORS.accent, color: "#fff", fontSize: 14, fontWeight: 600,
              fontFamily: "inherit", cursor: submitting ? "wait" : "pointer",
              opacity: submitting ? 0.7 : 1, transition: "opacity 0.15s",
            }}>
              {submitting ? "Submitting..." : "Submit for Approval"}
            </button>

            <p style={{ textAlign: "center", fontSize: 12, color: COLORS.textMuted, marginTop: 14, lineHeight: 1.5 }}>
              The careers team will review your request and approve access to the platform.
            </p>
          </form>
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button onClick={signOut} style={{
            background: "none", border: "none", color: COLORS.textMuted, fontSize: 12,
            fontFamily: "inherit", cursor: "pointer",
          }}>
            Log out ({profile?.email || ""})
          </button>
        </div>
      </div>
    </div>
  );
}
